import {MinusIcon, PlusIcon} from '@heroicons/react/solid';
import React, {useCallback, useEffect, useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';
import {hasAdminPermission} from '@afriex/core/utils';

import {
  Button,
  ConfirmationModal,
  CustomToast,
  Header,
  Pagination,
  Table,
  TableSkeleton,
  Tabs,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import {merchantService} from '@/services/merchants';
import type {TabItem} from '@/types';
import type {
  MarketPlaceAsk,
  MarketPlaceBid,
  MarketPlaceTradeQuery,
} from '@/types/MarketPlace';
import {MarketPlaceStatus} from '@/types/MarketPlace';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';

import AskModal from './components/AskModal';
import AskSummary from './components/AskSummary';
import BidTable from './components/BidTable';
import TradesTab from './Trades';
import OfferTradesSection from './Trades/OfferTradesSection';
import {formatAmount, formatCount, formatDecimal} from './Trades/utils/format';
import {PAGE_LIMIT} from './utils/constants';
import {formatOptionalDate, isExpired} from './utils/date';
import {getAskStatusLabel} from './utils/status';

type Toast = {variant: 'success' | 'error'; message: string};

const getAskStatusClassName = (
  ask: MarketPlaceAsk,
  isDeactivated: boolean,
): string => {
  if (isDeactivated) {
    return 'text-amber-600';
  }

  if (ask.status !== MarketPlaceStatus.OPEN) {
    return 'text-gray-500';
  }

  return isExpired(ask.expiresAt) ? 'text-red-600' : 'text-green-600';
};

const MarketPlaceScreen = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTabId, setActiveTabId] = useState('asks');
  const [toast, setToast] = useState<Toast | null>(null);
  // Set when an offer asks the Trades tab to show only that offer's trades.
  const [tradesAskId, setTradesAskId] = useState<number | null>(null);
  // The Trades tab's applied filters, kept here so a tab switch keeps them
  const [tradesFilters, setTradesFilters] = useState<MarketPlaceTradeQuery>({});

  // Asks state
  const [asks, setAsks] = useState<MarketPlaceAsk[]>([]);
  const [asksLoading, setAsksLoading] = useState(false);
  const [asksTotal, setAsksTotal] = useState(0);
  const [asksPage, setAsksPage] = useState(0);
  const [expandedAskId, setExpandedAskId] = useState<number | null>(null);

  // Ask actions state
  const [editAsk, setEditAsk] = useState<MarketPlaceAsk | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MarketPlaceAsk | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Bids state
  const [bids, setBids] = useState<MarketPlaceBid[]>([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [bidsTotal, setBidsTotal] = useState(0);
  const [bidsPage, setBidsPage] = useState(0);

  // Bid counter-offer action state
  const [bidActionTarget, setBidActionTarget] = useState<MarketPlaceBid | null>(
    null,
  );
  const [bidActionType, setBidActionType] = useState<
    'approve' | 'reject' | null
  >(null);

  const fetchAsks = useCallback(async (page: number) => {
    setAsksLoading(true);
    try {
      const result = await merchantService.getMarketPlaceAsks({
        page,
        limit: PAGE_LIMIT,
      });
      setAsks(result.data || []);
      setAsksTotal(result.total || 0);
    } catch {
      setAsks([]);
      setAsksTotal(0);
    } finally {
      setAsksLoading(false);
    }
  }, []);

  const fetchBids = useCallback(async (page: number) => {
    setBidsLoading(true);
    try {
      const result = await merchantService.getMarketPlaceBids({
        page,
        limit: PAGE_LIMIT,
      });
      setBids(result.data || []);
      setBidsTotal(result.total || 0);
    } catch {
      setBids([]);
      setBidsTotal(0);
    } finally {
      setBidsLoading(false);
    }
  }, []);

  // Every refresh on this screen is fire-and-forget: nothing below awaits the
  // list before continuing, and both fetchers already swallow their own
  // failures into an empty list, so these handlers only fire if that catch or
  // the finally itself throws. The adapters exist so that rejection has
  // somewhere to go instead of escaping unhandled, and `useCallback` keeps
  // their identity stable for the memoised callbacks that depend on them.
  const refreshAsks = useCallback(
    (page: number) => {
      fetchAsks(page).catch(logRejection('Failed to load marketplace offers'));
    },
    [fetchAsks],
  );

  const refreshBids = useCallback(
    (page: number) => {
      fetchBids(page).catch(
        logRejection('Failed to load marketplace counter offers'),
      );
    },
    [fetchBids],
  );

  useEffect(() => {
    refreshAsks(0);
    refreshBids(0);
  }, []);

  // Fetch data when tab changes
  const handleTabChange = useCallback(
    (tab: TabItem) => {
      setActiveTabId(tab.id);
      if (tab.id === 'asks') {
        refreshAsks(asksPage);
      } else if (tab.id === 'bids') {
        refreshBids(bidsPage);
      }
    },
    [refreshAsks, refreshBids, asksPage, bidsPage],
  );

  const handleAskPageChange = (page: number) => {
    setAsksPage(page);
    setExpandedAskId(null);
    refreshAsks(page);
  };

  const handleBidPageChange = (page: number) => {
    setBidsPage(page);
    refreshBids(page);
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    setAsksPage(0);
    setExpandedAskId(null);
    refreshAsks(0);
  };

  const handleEditSuccess = () => {
    setEditAsk(null);
    setToast({variant: 'success', message: 'Offer updated'});
    refreshAsks(asksPage);
  };

  // Disable / re-enable an ask. Blocked server-side when the ask has active
  // bids; the error surfaces via the toast.
  const handleToggleDisable = async (ask: MarketPlaceAsk) => {
    const isDeactivated = Boolean(ask.meta?.flags?.isDeactivated);
    setActionLoading(true);
    try {
      await merchantService.setMarketPlaceAskStatus(ask.id, !isDeactivated);
      setToast({
        variant: 'success',
        message: isDeactivated ? 'Offer enabled' : 'Offer disabled',
      });
      refreshAsks(asksPage);
    } catch (err) {
      setToast({
        variant: 'error',
        message: getErrorMessage(err, 'Failed to update offer'),
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Approve a business-posted offer so it becomes matchable. Business offers
  // land PENDING_ADMIN_APPROVAL because the business sets its own price.
  const handleApproveOffer = async (ask: MarketPlaceAsk) => {
    setActionLoading(true);
    try {
      await merchantService.approveMarketPlaceOffer(ask.id);
      setToast({variant: 'success', message: 'Offer approved'});
      refreshAsks(asksPage);
    } catch (err) {
      setToast({
        variant: 'error',
        message: getErrorMessage(err, 'Failed to approve offer'),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    setActionLoading(true);
    try {
      await merchantService.deleteMarketPlaceAsk(deleteTarget.id);
      setToast({variant: 'success', message: 'Offer deleted'});
      setDeleteTarget(null);
      refreshAsks(asksPage);
    } catch (err) {
      setToast({
        variant: 'error',
        message: getErrorMessage(err, 'Failed to delete offer'),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveBid = (bid: MarketPlaceBid) => {
    setBidActionTarget(bid);
    setBidActionType('approve');
  };

  const handleRejectBid = (bid: MarketPlaceBid) => {
    setBidActionTarget(bid);
    setBidActionType('reject');
  };

  const handleConfirmBidAction = async () => {
    if (!bidActionTarget || !bidActionType) {
      return;
    }
    setActionLoading(true);
    try {
      if (bidActionType === 'approve') {
        await merchantService.approveMarketPlaceBid(bidActionTarget.id);
        setToast({variant: 'success', message: 'Counter offer approved'});
      } else {
        await merchantService.rejectMarketPlaceBid(bidActionTarget.id);
        setToast({variant: 'success', message: 'Counter offer rejected'});
      }
      setBidActionTarget(null);
      setBidActionType(null);
      refreshBids(bidsPage);
    } catch (err) {
      setToast({
        variant: 'error',
        message: getErrorMessage(
          err,
          `Failed to ${bidActionType} counter offer`,
        ),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelBidAction = () => {
    setBidActionTarget(null);
    setBidActionType(null);
  };

  const toggleAskExpanded = (askId: number) => {
    setExpandedAskId((current) => (current === askId ? null : askId));
  };

  // Opening one offer's trades starts a fresh view of them.
  const handleViewOfferTrades = useCallback((askId: number) => {
    setTradesAskId(askId);
    setTradesFilters({});
    setActiveTabId('trades');
  }, []);

  const canViewTrades = hasAdminPermission(
    localStorage.getItem('admin-role') ?? undefined,
    AfriexAdminPermission.MarketPlaceRead,
  );

  const marketplaceTabs: TabItem[] = [
    {
      id: 'asks',
      title: 'Offers',
      content: (
        <section className="pt-5">
          {asksLoading ? (
            <TableSkeleton />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-20 px-4">&nbsp;</Th>
                  <Th>ID</Th>
                  <Th>Created By</Th>
                  <Th>Volume</Th>
                  <Th>Quote</Th>
                  <Th title="Filled volume and paid trades only. Settling and expired trades are not counted.">
                    Filled
                  </Th>
                  <Th>Min Volume</Th>
                  <Th>Status</Th>
                  <Th>Created At</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <TBody>
                {asks.length === 0 ? (
                  <Tr>
                    <Td colSpan={10} className="text-center text-gray-500 py-4">
                      No offers found
                    </Td>
                  </Tr>
                ) : (
                  asks.map((ask) => {
                    const isDeactivated = Boolean(
                      ask.meta?.flags?.isDeactivated,
                    );
                    const isClosed = ask.status === MarketPlaceStatus.CLOSED;
                    const isPendingApproval =
                      ask.status === MarketPlaceStatus.PENDING_ADMIN_APPROVAL;
                    const isExpanded = expandedAskId === ask.id;
                    return (
                      <React.Fragment key={ask.id}>
                        <Tr>
                          <Td isNumeric className="px-4">
                            <Button
                              onClick={() => toggleAskExpanded(ask.id)}
                              variant="unStyled"
                            >
                              <div
                                className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                                  isExpanded && 'bg-cyan-50'
                                }`}
                              >
                                {isExpanded ? (
                                  <MinusIcon className="w-4 h-4" />
                                ) : (
                                  <PlusIcon className="w-4 h-4" />
                                )}
                              </div>
                            </Button>
                          </Td>
                          <Td>{ask.id}</Td>
                          <Td>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {ask.createdByName || '-'}
                              </span>
                              {ask.createdByRole && (
                                <span className="text-xs text-gray-500">
                                  {ask.createdByRole.replace(/_/g, ' ')}
                                </span>
                              )}
                            </div>
                          </Td>
                          <Td>
                            {ask.volume.value} {ask.volume.asset}
                          </Td>
                          <Td>
                            {ask.quote.value} {ask.quote.asset}
                          </Td>
                          <Td>
                            {ask.filledVolume ? (
                              <div className="flex flex-col">
                                <span>
                                  {formatDecimal(ask.filledVolume.value)} /{' '}
                                  {formatAmount(ask.volume)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatCount(ask.tradeCount ?? 0)} paid{' '}
                                  {(ask.tradeCount ?? 0) === 1
                                    ? 'trade'
                                    : 'trades'}
                                </span>
                              </div>
                            ) : (
                              '-'
                            )}
                          </Td>
                          <Td>
                            {ask.meta?.minBidVolume
                              ? `${ask.meta.minBidVolume} ${ask.volume.asset}`
                              : '-'}
                          </Td>
                          <Td>
                            <span
                              className={getAskStatusClassName(
                                ask,
                                isDeactivated,
                              )}
                            >
                              {getAskStatusLabel(ask)}
                            </span>
                          </Td>
                          <Td>{formatOptionalDate(ask.createdAt)}</Td>
                          <Td>
                            <div className="flex gap-2">
                              {isPendingApproval && (
                                <Button
                                  variant="outline"
                                  colorScheme="green"
                                  onClick={() => handleApproveOffer(ask)}
                                  disabled={actionLoading}
                                >
                                  Approve
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                colorScheme="cyan"
                                onClick={() => setEditAsk(ask)}
                                disabled={isClosed}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                colorScheme={isDeactivated ? 'green' : 'yellow'}
                                onClick={() => handleToggleDisable(ask)}
                                disabled={isClosed}
                              >
                                {isDeactivated ? 'Enable' : 'Disable'}
                              </Button>
                              <Button
                                variant="outline"
                                colorScheme="red"
                                onClick={() => setDeleteTarget(ask)}
                                disabled={isClosed}
                              >
                                Delete
                              </Button>
                            </div>
                          </Td>
                        </Tr>

                        {isExpanded && (
                          <Tr enableHover={false}>
                            <Td colSpan={10} className="p-0 m-0">
                              <div className="flex flex-col gap-6 py-5 sm:py-10 px-8 bg-sky-50 dark:bg-transparent w-full">
                                <div className="flex justify-center">
                                  <AskSummary ask={ask} />
                                </div>
                                {canViewTrades && (
                                  <OfferTradesSection
                                    ask={ask}
                                    onViewAllTrades={handleViewOfferTrades}
                                  />
                                )}
                              </div>
                            </Td>
                          </Tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TBody>
            </Table>
          )}
          {asksTotal > PAGE_LIMIT && (
            <Pagination
              onPageChange={
                handleAskPageChange as PaginationProps['onPageChange']
              }
              total={asksTotal}
              isSearch={false}
              isPageReset={false}
            />
          )}
        </section>
      ),
    },
    {
      id: 'bids',
      title: 'Bids',
      content: (
        <section className="pt-5">
          {bidsLoading ? (
            <TableSkeleton />
          ) : (
            <BidTable
              bids={bids}
              onApprove={handleApproveBid}
              onReject={handleRejectBid}
            />
          )}
          {bidsTotal > PAGE_LIMIT && (
            <Pagination
              onPageChange={
                handleBidPageChange as PaginationProps['onPageChange']
              }
              total={bidsTotal}
              isSearch={false}
              isPageReset={false}
            />
          )}
        </section>
      ),
    },
  ];

  if (canViewTrades) {
    marketplaceTabs.push({
      id: 'trades',
      title: 'Trades',
      content: (
        <TradesTab
          askId={tradesAskId}
          onClearAskId={() => setTradesAskId(null)}
          appliedFilters={tradesFilters}
          setAppliedFilters={setTradesFilters}
        />
      ),
    });
  }

  return (
    <main className="my-5 mx-4 md:mx-10">
      <div className="flex items-center justify-between">
        <Header title="Merchant Marketplace" />
        <Button
          variant="solid"
          colorScheme="cyan"
          onClick={() => setShowCreateModal(true)}
        >
          Create Offer +
        </Button>
      </div>

      {toast && (
        <div className="mt-3">
          <CustomToast
            variant={toast.variant}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        </div>
      )}

      <div className="mt-5">
        <Tabs
          tabs={marketplaceTabs}
          activeTabId={activeTabId}
          onTabChange={handleTabChange}
        />
      </div>

      <AskModal
        mode="create"
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      <AskModal
        mode="edit"
        isOpen={Boolean(editAsk)}
        ask={editAsk}
        onClose={() => setEditAsk(null)}
        onSuccess={handleEditSuccess}
      />

      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        title="Delete Offer"
        label={`Delete offer #${deleteTarget?.id ?? ''}? This cannot be undone. Offers with active bids cannot be deleted.`}
        confirmActionLabel="Delete"
        cancelActionLabel="Cancel"
        loadingLabel="Deleting..."
        loading={actionLoading}
        confirmAction={handleConfirmDelete}
        cancelAction={() => setDeleteTarget(null)}
      />

      <ConfirmationModal
        isOpen={Boolean(bidActionTarget)}
        title={
          bidActionType === 'approve'
            ? 'Approve Counter Offer'
            : 'Reject Counter Offer'
        }
        label={`${
          bidActionType === 'approve' ? 'Approve' : 'Reject'
        } counter offer for bid #${bidActionTarget?.id ?? ''}?`}
        confirmActionLabel={bidActionType === 'approve' ? 'Approve' : 'Reject'}
        cancelActionLabel="Cancel"
        loadingLabel={
          bidActionType === 'approve' ? 'Approving...' : 'Rejecting...'
        }
        loading={actionLoading}
        confirmAction={handleConfirmBidAction}
        cancelAction={handleCancelBidAction}
      />
    </main>
  );
};

export default MarketPlaceScreen;
